import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Hook to check feature access for the current admin
 * Returns access permissions based on subscription status and SA settings
 */
export const useFeatureAccess = () => {
  const { user } = useAuth();
  const [featureAccess, setFeatureAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id && user?.role === "admin") {
      fetchFeatureAccess();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchFeatureAccess = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/admin/${user.id}/feature-access`);
      setFeatureAccess(res.data);
    } catch (err) {
      console.error("Failed to fetch feature access:", err);
      // Default to allowing everything if API fails
      setFeatureAccess({
        is_trial: false,
        feature_access: {
          vehicle_settings: { can_save: true, reason: null },
          surge_pricing: { can_save: true, reason: null },
          branding: { can_save: true, reason: null },
          promo_codes: { can_save: true, reason: null },
          themes: { can_save: true, reason: null }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if a specific feature can be saved
   * @param {string} feature - One of: vehicle_settings, surge_pricing, branding, promo_codes, themes
   * @returns {{ canSave: boolean, reason: string | null }}
   */
  const canSaveFeature = (feature) => {
    if (!featureAccess) return { canSave: true, reason: null };
    const access = featureAccess.feature_access?.[feature];
    return {
      canSave: access?.can_save ?? true,
      reason: access?.reason ?? null
    };
  };

  /**
   * Check if admin is on trial
   */
  const isTrial = featureAccess?.is_trial ?? false;

  return {
    featureAccess,
    loading,
    canSaveFeature,
    isTrial,
    refetch: fetchFeatureAccess
  };
};

export default useFeatureAccess;
